/**
 * What a printed label says, and what a scan means.
 *
 * A label carries the address of the thing, not the thing: scanning an item's
 * label with any phone camera opens that item in the app, and the in-app
 * scanner reads the same code without leaving it. Nothing about the item is in
 * the code, so a label on a shelf discloses nothing to whoever reads it, and
 * the app's sign-in still stands between them and the item.
 */

export type LabelKind = "item" | "location";

export const labelPath = (kind: LabelKind, id: string): string =>
  `/${kind === "item" ? "item" : "locations"}/${encodeURIComponent(id)}`;

export const labelUrl = (origin: string, kind: LabelKind, id: string): string =>
  `${origin.replace(/\/+$/, "")}${labelPath(kind, id)}`;

export type ScanTarget =
  { kind: "item"; id: string } | { kind: "location"; id: string } | { kind: "text"; text: string };

const OWN_ADDRESS = /^(?:https?:\/\/[^/\s]+)?\/(item|locations)\/([^/?#\s]+)\/?(?:[?#].*)?$/;

/**
 * A scanned or typed value: one of our own addresses names a thing; anything
 * else (a serial number off a barcode, a vendor's label) is text to search for.
 * Null when there is nothing there.
 */
export function parseScan(raw: string): ScanTarget | null {
  const text = raw.trim();
  if (!text) return null;

  const match = OWN_ADDRESS.exec(text);
  if (match) {
    try {
      const id = decodeURIComponent(match[2] as string);
      return match[1] === "item" ? { kind: "item", id } : { kind: "location", id };
    } catch {
      return { kind: "text", text };
    }
  }
  return { kind: "text", text };
}
